import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Team Manual — staff-only, long-form reading.
//
// 🚨 THE CONTENT IS FETCHED, NEVER IMPORTED. Nothing in this file may import a
// manual markdown file, and no markdown renderer belongs in this bundle. An
// import would put the full text into build/ — inlined into main.<hash>.js, or
// copied verbatim into build/static/media/ by CRA's asset/resource fallback and
// served as a fetchable file — where app.godoorly.com would hand it to anyone
// who asked. The auth gate would then be cosmetic: the page would refuse to
// render while the document it protects sat in the open.
// backend/scripts/verify-manual-not-bundled.js fails the build if that ever
// happens; it greps ALL of build/, not just the JS.
//
// ⚠️ THE API IS THE GATE. This page deliberately does NOT wrap in AdminOnly.
// That guard reads doorly_admin_user out of localStorage, which any visitor can
// write, so it decides rendering and grants nothing — its own source says so.
// GET /api/manual is behind requireAdmin (the adminAuth path, which resolves the
// staff row from the database). An unauthenticated visitor gets a 401, which the
// AdminLayout interceptor turns into a redirect to the staff login.

// ⚠️ THREE STATES THAT MUST NOT COLLAPSE INTO EACH OTHER. "Loading" and "the
// request failed" both have no content to show, and rendering the same empty
// frame for both is how a fetch failure becomes an apparently-empty manual —
// which reads as "there is nothing here" rather than "we could not load it".
const STATE = { LOADING: 'loading', LOADED: 'loaded', ERROR: 'error' };

const CARD = {
  background: '#fff',
  border: '1px solid #f3f4f6',
  borderRadius: '16px',
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
};

export default function AdminManual() {
  const token = localStorage.getItem('doorly_admin_token');
  const [state, setState] = useState(STATE.LOADING);
  const [tabs, setTabs] = useState([]);
  const [version, setVersion] = useState(null);
  const [active, setActive] = useState(null);
  const [ack, setAck] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);

  // ⚠️ THE HASH IS THE DEEP-LINK CONTRACT — /manual#compliance. Slugs come from
  // the filename with the ordering prefix stripped, so renumbering a file to
  // move it in the sidebar does not break a link a colleague already sent.
  const hashTab = () => (typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '');

  const load = useCallback(async () => {
    setState(STATE.LOADING);
    try {
      const res = await axios.get(`${API_URL}/api/manual`, { headers: { Authorization: `Bearer ${token}` } });
      const list = res.data.tabs || [];
      setTabs(list);
      setVersion(res.data.version);
      const wanted = hashTab();
      setActive(list.some((t) => t.id === wanted) ? wanted : (list[0] && list[0].id) || null);
      setState(STATE.LOADED);
      try {
        const a = await axios.get(`${API_URL}/api/manual/acknowledgement`, { headers: { Authorization: `Bearer ${token}` } });
        setAck(a.data);
      } catch (_) {
        // The acknowledgement state failing is NOT a reason to hide the manual.
        // Worst case the control shows unacknowledged and a second click is a
        // no-op at the unique index.
        setAck(null);
      }
    } catch (_) {
      setState(STATE.ERROR);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Back/forward and pasted links both arrive as hashchange.
  useEffect(() => {
    const onHash = () => { const h = hashTab(); if (h) setActive((prev) => (h === prev ? prev : h)); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const select = (id) => {
    setActive(id);
    if (typeof window !== 'undefined') window.history.replaceState(null, '', `#${id}`);
  };

  const acknowledge = async () => {
    setAckBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/manual/acknowledgement`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAck({ version: res.data.version, acknowledged: true, acknowledged_at: res.data.acknowledged_at });
    } catch (_) { /* leave the control in place so it can be retried */ }
    setAckBusy(false);
  };

  const current = tabs.find((t) => t.id === active) || null;
  const isLast = current && tabs.length > 0 && tabs[tabs.length - 1].id === current.id;

  if (state === STATE.LOADING) {
    return (
      <AdminLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ ...CARD, padding: '24px' }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ height: i === 0 ? 28 : 14, width: i === 0 ? '40%' : `${90 - i * 8}%`,
              background: '#f3f4f6', borderRadius: 8, marginBottom: 14 }} />
          ))}
        </div>
      </div>
      </AdminLayout>
    );
  }

  if (state === STATE.ERROR) {
    return (
      <AdminLayout>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ ...CARD, padding: '28px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 6 }}>Couldn’t load the Team Manual</div>
          {/* Explicitly NOT an empty manual. The distinction matters: a blank
              page would read as "there is nothing here". */}
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 18, lineHeight: 1.6 }}>
            The request failed. Nothing has been lost — this is a loading problem, not an empty manual.
          </div>
          <button onClick={load}
            style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Team Manual</h1>
        <div style={{ fontSize: 12, color: '#9ca3af' }}>Internal · version {version}</div>
      </div>

      {/* Mobile: a select. People will read this on a phone, and a vertical rail
          of tabs on a 375px screen costs the whole viewport before any text. */}
      <div className="manual-mobile-nav" style={{ marginBottom: 16 }}>
        <select value={active || ''} onChange={(e) => select(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10,
            fontSize: 14, background: '#fff', color: '#111827' }}>
          {tabs.map((t) => <option key={t.id} value={t.id}>{t.tab}</option>)}
        </select>
      </div>

      <div className="manual-layout" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <nav className="manual-rail" style={{ width: 220, flexShrink: 0, position: 'sticky', top: 16 }}>
          <div style={{ ...CARD, padding: 8 }}>
            {tabs.map((t) => (
              <button key={t.id} onClick={() => select(t.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                  background: t.id === active ? '#eff6ff' : 'transparent',
                  color: t.id === active ? '#3a6ad9' : '#374151',
                  fontWeight: t.id === active ? 700 : 500,
                  fontSize: 13, padding: '9px 12px', borderRadius: 10, marginBottom: 2 }}>
                {t.tab}
              </button>
            ))}
          </div>
        </nav>

        <article style={{ ...CARD, padding: '28px 32px', flex: 1, minWidth: 0 }}>
          {/* ⚠️ ~68ch. This is long-form reading, not a dashboard: full-width
              prose on a wide monitor is measurably harder to read, and the rest
              of DoorlyDesk's density is wrong for a document. */}
          <div className="manual-body" style={{ maxWidth: '68ch' }}
            dangerouslySetInnerHTML={{ __html: current ? current.html : '' }} />

          {isLast && (
            <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f3f4f6', maxWidth: '68ch' }}>
              {ack && ack.acknowledged ? (
                <div style={{ fontSize: 13, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 12, padding: '12px 16px' }}>
                  ✓ Acknowledged version {ack.version}
                  {ack.acknowledged_at ? ` on ${new Date(ack.acknowledged_at).toLocaleDateString()}` : ''}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 12, lineHeight: 1.6 }}>
                    Confirm you have read the Team Manual. This is recorded against version {version};
                    if the manual is revised you will be asked again.
                  </div>
                  <button onClick={acknowledge} disabled={ackBusy}
                    style={{ background: ackBusy ? '#9ca3af' : '#3a6ad9', color: '#fff', border: 'none',
                      borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700,
                      cursor: ackBusy ? 'not-allowed' : 'pointer' }}>
                    {ackBusy ? 'Recording…' : 'I have read the Team Manual'}
                  </button>
                </>
              )}
            </div>
          )}
        </article>
      </div>

      <style>{`
        .manual-mobile-nav { display: none; }
        @media (max-width: 820px) {
          .manual-mobile-nav { display: block; }
          .manual-rail { display: none; }
          .manual-layout { display: block; }
        }
        .manual-body { font-size: 15px; line-height: 1.75; color: #374151; }
        .manual-body h1 { font-size: 24px; font-weight: 800; color: #111827; margin: 0 0 14px; }
        .manual-body h2 { font-size: 18px; font-weight: 800; color: #111827; margin: 28px 0 10px; }
        .manual-body h3 { font-size: 15px; font-weight: 700; color: #111827; margin: 22px 0 8px; }
        .manual-body p  { margin: 0 0 14px; }
        .manual-body ul, .manual-body ol { margin: 0 0 14px; padding-left: 22px; }
        .manual-body li { margin-bottom: 6px; }
        .manual-body a { color: #3a6ad9; }
        .manual-body code { background: #f3f4f6; padding: 1px 5px; borderRadius: 4px; font-size: 13px; }
        .manual-body table { border-collapse: collapse; width: 100%; margin: 0 0 16px; display: block; overflow-x: auto; }
        .manual-body th, .manual-body td { border: 1px solid #f3f4f6; padding: 8px 10px; text-align: left; font-size: 13px; }
        .manual-body blockquote { margin: 0 0 14px; padding: 10px 16px; background: #f9fafb; border-left: 3px solid #3a6ad9; }
      `}</style>
    </div>
    </AdminLayout>
  );
}
